import { USER_ROLES } from "@/lib/constants/user-types";

/**
 * PURE route-access rules used by the middleware (`proxy.ts` via
 * `middleware-utils.ts`). Kept free of `next/server` / Supabase imports so it is
 * unit-testable in a jsdom/node test env (importing `next/server` requires the
 * Edge `Request` global, which the test env lacks). `middleware-utils.ts`
 * re-exports everything here.
 */

/** Determine redirect path based on user role. */
export function getRedirectPathForRole(role: string | null): string {
  if (role === USER_ROLES.CLIENT_USER) {
    return "/contacts/dashboard";
  }
  return "/dashboard";
}

/** Check if route is public (no auth required). */
export function isPublicRoute(pathname: string): boolean {
  const publicRoutes = ["/crm/calendar/public-booking"];
  return publicRoutes.some((route) => pathname.startsWith(route));
}

/** Check if route is protected (requires auth). */
export function isProtectedRoute(pathname: string): boolean {
  const protectedRoutes = [
    "/dashboard",
    "/admin",
    "/chat",
    "/tickets",
    "/time-tracking",
    "/reports",
    "/contacts",
    "/contracts",
    "/crm",
    "/companies",
    "/recurring-transactions",
    "/vault",
  ];
  return protectedRoutes.some((route) => pathname.startsWith(route));
}

/** Check if route is an auth route. */
export function isAuthRoute(pathname: string): boolean {
  const authRoutes = ["/auth/employee", "/auth/client", "/auth/callback"];
  return authRoutes.some((route) => pathname.startsWith(route));
}

/**
 * Role-based access for specific route prefixes. Iteration order matters:
 * `hasRouteAccess` returns on the FIRST prefix match, so a more-specific rule
 * MUST be listed before a broader one that would also match.
 */
export const ROLE_BASED_ROUTES = {
  "/admin": [USER_ROLES.WTC_ADMIN],
  // Clients may view companies — RLS (`companies_client_read`) scopes them to
  // ONLY their own company (a company where they have contact access); SELECT
  // only, no client write. Sensitive columns (financials, QB refs, internal
  // notes, slack channel) are additionally hidden by field `allowedRoles`.
  "/companies": [
    USER_ROLES.WTC_ADMIN,
    USER_ROLES.WTC_EMPLOYEE,
    USER_ROLES.WTC_CRM,
    USER_ROLES.CLIENT_USER,
  ],
  "/offerings": [USER_ROLES.WTC_ADMIN, USER_ROLES.WTC_EMPLOYEE, USER_ROLES.WTC_CRM],
  // Clients may view THEIR OWN time entries (RLS-scoped). This more-specific
  // rule MUST stay ordered before "/time-tracking": `hasRouteAccess` returns on
  // the FIRST prefix match, so the broad employee-only rule below would
  // otherwise capture it and bounce clients to /contacts/dashboard.
  "/time-tracking/entries": [
    USER_ROLES.WTC_ADMIN,
    USER_ROLES.WTC_EMPLOYEE,
    USER_ROLES.WTC_CRM,
    USER_ROLES.CLIENT_USER,
  ],
  "/time-tracking": [USER_ROLES.WTC_ADMIN, USER_ROLES.WTC_EMPLOYEE, USER_ROLES.WTC_CRM],
  "/recurring-transactions": [USER_ROLES.WTC_ADMIN],
  "/chat": [USER_ROLES.WTC_ADMIN, USER_ROLES.WTC_EMPLOYEE, USER_ROLES.WTC_CRM],
  // Credential vault UI (vault Phase 5b): WTC staff only, never CRM or client
  // users. The vault itself then enforces per-folder permissions per user.
  // `/vault/admin` deliberately inherits this STAFF gate, not a Hub-admin one
  // (vault review L7): who is a VAULT admin is the vault's `vault-admins` group
  // (D-17), not Hub's `wtc_admin` role, and every admin action there (approve an
  // enrollment, activate a second confirm device, list the group) is refused by
  // the vault itself — with the caller's own token and live unlock — unless the
  // caller is in that group. The page only renders the vault's answer.
  "/vault": [USER_ROLES.WTC_ADMIN, USER_ROLES.WTC_EMPLOYEE],
} as const;

/** Check if a user role has access to a specific route. */
export function hasRouteAccess(pathname: string, userRole: string | null): boolean {
  for (const [route, allowedRoles] of Object.entries(ROLE_BASED_ROUTES)) {
    if (pathname.startsWith(route)) {
      return userRole ? [...allowedRoles].includes(userRole as never) : false;
    }
  }
  return true; // No specific role requirement
}
